import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Download, Search, ShieldCheck } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CountryBadge,
  CountrySelect,
  EmptyState,
  OperatorBadge,
  OperatorSelect,
  PageHeader,
  ProductIntro,
  TxnId,
  SkeletonRow,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { downloadCsv } from '@/lib/utils/csvExport';
import type { ResponseEnvelope } from '@/types/api';

interface VerificationRecord {
  id: string;
  mobileNumber: string | null;
  isNumberValid: boolean;
  operatorId: string | null;
  countryCode: string | null;
  serviceType: string | null;
  productType: string | null;
  latencyMs: number | null;
  createdAt: string;
}

interface VerificationsPage {
  content: VerificationRecord[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/** Common verification stages, offered as suggestions for the Stage filter. */
const STAGE_SUGGESTIONS = [
  'LOGIN',
  'REGISTRATION',
  'SIGNUP',
  'RESET_PASSWORD',
  'ACCOUNT_RECOVERY',
  'TRANSACTION',
  'CHECKOUT',
  'ONBOARDING',
];

interface VerificationFilters {
  page: number;
  size: number;
  status?: boolean;
  transactionId?: string;
  operator?: string;
  countryCode?: string;
  productType?: string;
  serviceType?: string;
  startDate?: string;
  endDate?: string;
}

async function fetchMyVerifications(params: VerificationFilters): Promise<VerificationsPage> {
  const { startDate, endDate, ...rest } = params;
  const res = await api.get<ResponseEnvelope<VerificationsPage>>(Endpoints.me.verifications, {
    params: {
      ...rest,
      // datetime-local values are local time — send full ISO (UTC) so the range is by time, not whole days.
      ...(startDate ? { startDate: new Date(startDate).toISOString() } : {}),
      ...(endDate ? { endDate: new Date(endDate).toISOString() } : {}),
    },
  });
  return unwrap(res);
}

function productTypeLabel(productType: string | null): string {
  return productType === 'VERIFY_N_P' ? 'Verify Number Plus' : 'Verify Number';
}

function isVerifyPlus(productType: string | null): boolean {
  return productType === 'VERIFY_N_P';
}

/** Turn a raw serviceType (e.g. "RESET_PASSWORD") into a readable stage ("Reset Password"). */
function stageLabel(serviceType: string | null): string {
  if (!serviceType || !serviceType.trim()) return '—';
  return serviceType
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ClientVerifyRequestsPage() {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [transactionId, setTransactionId] = useState('');
  const [operator, setOperator] = useState('');
  const [country, setCountry] = useState('');
  const [productType, setProductType] = useState('');
  const [stage, setStage] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const baseFilters: Omit<VerificationFilters, 'page' | 'size'> = {
    status: statusFilter === 'all' ? undefined : statusFilter === 'success',
    transactionId: transactionId || undefined,
    operator: operator || undefined,
    countryCode: country || undefined,
    productType: productType || undefined,
    serviceType: stage.trim() || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  const query = useQuery({
    queryKey: ['me', 'verifications', page, statusFilter, transactionId, operator, country, productType, stage, startDate, endDate],
    queryFn: () => fetchMyVerifications({ page, size: 25, ...baseFilters }),
  });

  const rows = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 1;
  const hasActiveFilters =
    statusFilter !== 'all' || !!transactionId || !!operator || !!country || !!productType || !!stage || !!startDate || !!endDate;

  function resetFilters() {
    setStatusFilter('all');
    setTransactionId('');
    setOperator('');
    setCountry('');
    setProductType('');
    setStage('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const exportData = await fetchMyVerifications({ page: 0, size: 5000, ...baseFilters });
      const headers = ['ID', 'Mobile', 'Operator', 'Country', 'Stage', 'Product Type', 'Result', 'Latency (ms)', 'Timestamp'];
      const csvRows = exportData.content.map((r) => [
        r.id,
        displayMobile(r.mobileNumber),
        r.operatorId ?? '',
        r.countryCode ?? '',
        r.serviceType ?? '',
        productTypeLabel(r.productType),
        r.isNumberValid ? 'MATCH' : 'NO MATCH',
        r.latencyMs ?? '',
        r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
      ]);
      downloadCsv(`verify-requests-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Verification Log"
        subtitle="Your verification history — newest first."
        badge={<Badge tone="primary">Authentication</Badge>}
        actions={
          <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting || (query.data?.totalElements ?? 0) === 0}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
      />

      <ProductIntro
        icon={ShieldCheck}
        tone="verify"
        eyebrow="Authentication · Verify & Verify+"
        title="Confirm a customer controls their mobile number — silently, with no SMS code to type."
        points={['No OTP to type', 'Operator-verified', 'Charged on pass or fail', 'Can’t be phished']}
      >
        <p>
          <strong>Verify</strong> asks the mobile network operator to confirm that the SIM in the device making
          the request actually owns the number the customer claims. It replaces the SMS one-time-code step that
          users abandon, costs less than a per-message SMS bill, and can’t be phished or intercepted like a
          typed code.
        </p>
        <p>
          <strong>Verify+</strong> does everything Verify does and also returns a privacy-safe masked hint of the
          confirmed number (e.g. ending in <code>47</code>), so you can show a reassuring &ldquo;your number
          ending in&hellip;&rdquo; confirmation without ever exposing the full number on screen.
        </p>
        <p>
          Each row below is one verification event — the number checked, its operator and country, the
          <strong> stage</strong> of your journey it ran at (Login, Signup, Reset Password&hellip;), whether it
          matched, and how fast it responded.
        </p>
      </ProductIntro>

      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex items-center gap-1.5 rounded-lg border border-border bg-surface-subtle p-1">
            {(['all', 'success', 'failed'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => { setStatusFilter(f); setPage(0); }}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                  statusFilter === f
                    ? 'bg-surface shadow-sm text-fg ring-1 ring-border'
                    : 'text-fg-muted hover:text-fg'
                }`}
              >
                {f === 'all' ? 'All' : f === 'success' ? 'Match' : 'No match'}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
            <input
              type="search" placeholder="Transaction ID…" value={transactionId}
              onChange={(e) => { setTransactionId(e.target.value); setPage(0); }}
              className="h-8 w-full rounded-lg border border-border bg-surface pl-8 pr-3 text-xs outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
          <button type="button" onClick={resetFilters} disabled={!hasActiveFilters}
            className="ml-auto text-xs text-fg-muted hover:text-fg underline-offset-2 hover:underline disabled:opacity-40 disabled:no-underline">
            Reset
          </button>
        </div>

        {/* Column filters: operator, country, product type, stage, and a by-time date range */}
        <div className="mt-3 flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:flex-wrap sm:items-center">
          <OperatorSelect
            value={operator}
            onChange={(v) => { setOperator(v); setPage(0); }}
            countryCode={country || undefined}
            className="h-8 min-w-[140px] py-0 text-xs"
          />
          <CountrySelect
            value={country}
            onChange={(v) => { setCountry(v); setPage(0); }}
            className="h-8 min-w-[140px] py-0 text-xs"
          />
          <select
            value={productType}
            onChange={(e) => { setProductType(e.target.value); setPage(0); }}
            className="h-8 min-w-[150px] rounded-lg border border-border bg-surface px-2 text-xs text-fg outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="">All Product Types</option>
            <option value="VERIFY_N">Verify Number</option>
            <option value="VERIFY_N_P">Verify Number Plus</option>
          </select>
          <div className="relative min-w-[150px]">
            <input
              type="search" list="stage-suggestions" placeholder="Stage (e.g. Login)…" value={stage}
              onChange={(e) => { setStage(e.target.value); setPage(0); }}
              className="h-8 w-full rounded-lg border border-border bg-surface px-2.5 text-xs outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            />
            <datalist id="stage-suggestions">
              {STAGE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-fg-subtle">
            <span className="hidden sm:inline">From</span>
            <input type="datetime-local" value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              className="h-8 rounded-lg border border-border bg-surface px-2 text-xs text-fg outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20" />
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-fg-subtle">
            <span className="hidden sm:inline">To</span>
            <input type="datetime-local" value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              className="h-8 rounded-lg border border-border bg-surface px-2 text-xs text-fg outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20" />
          </label>
          {query.data && (
            <span className="ml-auto text-xs text-fg-subtle">{query.data.totalElements.toLocaleString()} records</span>
          )}
        </div>
      </Card>

      <Card noPadding>
        {query.isLoading ? (
          <div>
            {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} cols={9} />)}
          </div>
        ) : query.isError ? (
          <div className="px-5 py-8">
            <div role="alert" className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
              <p className="text-rose-700 dark:text-rose-300">
                {isApiError(query.error) ? query.error.firstErrorMessage : 'Could not load verifications.'}
              </p>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            message={
              hasActiveFilters
                ? 'No results match your current filters. Try clearing some filters.'
                : 'No verification requests yet. Your integration calls will appear here.'
            }
          />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Transaction ID</TH>
                  <TH>Mobile</TH>
                  <TH>Operator</TH>
                  <TH>Country</TH>
                  <TH>Stage</TH>
                  <TH>Product Type</TH>
                  <TH>Result</TH>
                  <TH className="text-right">Latency</TH>
                  <TH>Timestamp</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TR key={row.id}>
                    <TD><TxnId value={row.id} /></TD>
                    <TD className="font-mono text-xs">{displayMobile(row.mobileNumber)}</TD>
                    <TD><OperatorBadge operator={row.operatorId} /></TD>
                    <TD><CountryBadge countryCode={row.countryCode} /></TD>
                    <TD>
                      {row.serviceType?.trim()
                        ? <Badge tone="neutral">{stageLabel(row.serviceType)}</Badge>
                        : <span className="text-xs text-fg-subtle">—</span>}
                    </TD>
                    <TD>
                      <Badge tone={isVerifyPlus(row.productType) ? 'warning' : 'primary'}>
                        {productTypeLabel(row.productType)}
                      </Badge>
                    </TD>
                    <TD>
                      <Badge tone={row.isNumberValid ? 'success' : 'danger'}>
                        {row.isNumberValid ? 'MATCH' : 'NO MATCH'}
                      </Badge>
                    </TD>
                    <TD className="text-right text-xs text-fg-muted">{row.latencyMs != null ? `${row.latencyMs}ms` : '—'}</TD>
                    <TD className="text-[11px] text-fg-muted">{new Date(row.createdAt).toLocaleString()}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-border px-4 py-3 sm:px-5">
                <span className="text-xs text-fg-muted">Page {page + 1} of {totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// Backend always returns pre-masked values; display as-is.
// VERIFY_N → "************", VERIFY_N_P → "**********47"
function displayMobile(mobile: string | null | undefined): string {
  return mobile ?? '—';
}
